/**
 * Image brand metrics card with prominence-based display
 * Shows logo/product presence, prominence, and brand identifiability
 */

import { Card } from '@/components/ui/Card';
import { cn } from '@/lib/utils';
import type { ImageBrandMetrics } from '@/types';

export interface ImageBrandMetricsCardProps {
  brandMetrics: ImageBrandMetrics;
  className?: string;
}

/**
 * Get badge styles for prominence levels
 */
function getProminenceBadge(prominence: string) {
  const styles: Record<string, { bg: string; text: string }> = {
    dominant: { bg: 'bg-green-100', text: 'text-green-700' },
    visible: { bg: 'bg-blue-100', text: 'text-blue-700' },
    subtle: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
    absent: { bg: 'bg-red-100', text: 'text-red-700' },
  };

  const defaultStyle = { bg: 'bg-red-100', text: 'text-red-700' };
  const style = styles[prominence] ?? defaultStyle;

  return (
    <span
      className={cn(
        'px-2 py-0.5 text-xs font-medium rounded capitalize',
        style.bg,
        style.text
      )}
    >
      {prominence}
    </span>
  );
}

/**
 * Present/absent indicator
 */
function PresenceIndicator({ present }: { present: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={cn(
          'w-2.5 h-2.5 rounded-full',
          present ? 'bg-green-500' : 'bg-red-400'
        )}
      />
      <span
        className={cn(
          'text-xs font-medium',
          present ? 'text-green-700' : 'text-red-600'
        )}
      >
        {present ? 'Present' : 'Absent'}
      </span>
    </div>
  );
}

export function ImageBrandMetricsCard({
  brandMetrics,
  className,
}: ImageBrandMetricsCardProps) {
  return (
    <Card className={cn('p-6', className)}>
      <h3 className="text-sm font-semibold text-gray-900 mb-6">
        Brand Metrics
      </h3>

      <div className="space-y-6">
        {/* Logo */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Logo</span>
            <div className="flex items-center gap-3">
              <PresenceIndicator present={brandMetrics.logoPresent} />
              {getProminenceBadge(brandMetrics.logoProminence)}
            </div>
          </div>
          {brandMetrics.logoPresent && brandMetrics.logoLocation && (
            <p className="text-xs text-gray-500 ml-0.5">
              Location: {brandMetrics.logoLocation}
            </p>
          )}
        </div>

        {/* Product */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Product</span>
            <div className="flex items-center gap-3">
              <PresenceIndicator present={brandMetrics.productPresent} />
              {getProminenceBadge(brandMetrics.productProminence)}
            </div>
          </div>
        </div>

        {/* Brand Identifiable at Glance */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <div className="text-sm font-medium text-gray-700">
              Brand Identifiable at Glance
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              Can the brand be recognized instantly?
            </div>
          </div>
          <div
            className={cn(
              'px-4 py-2 rounded-full font-bold text-sm uppercase',
              brandMetrics.brandIdentifiableAtGlance
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            )}
          >
            {brandMetrics.brandIdentifiableAtGlance ? 'Yes' : 'No'}
          </div>
        </div>

        {/* Distinctive Assets */}
        {brandMetrics.distinctiveAssetsPresent.length > 0 && (
          <div>
            <span className="text-sm font-medium text-gray-700 block mb-2">
              Distinctive Assets
            </span>
            <div className="flex flex-wrap gap-1.5">
              {brandMetrics.distinctiveAssetsPresent.map((asset, index) => (
                <span
                  key={index}
                  className="px-2.5 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700"
                >
                  {asset}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}
